import {
  ArrowDropDown,
  ArrowDropUp,
  Email,
  ListAltOutlined,
  MarkunreadMailbox,
  Phone,
  Help,
} from '@mui/icons-material';
import {
  Box,
  Button,
  ButtonBase,
  Fade,
  List,
  Divider,
  ListItem,
  ListItemAvatar,
  Avatar,
  ListItemIcon,
  Modal,
  Paper,
  Snackbar,
  SnackbarCloseReason,
  Typography,
  useMediaQuery,
  Theme,
} from '@mui/material';
import Alert from '@mui/material/Alert';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import React, { SyntheticEvent, useState } from 'react';
import { useSupportMenuStyles } from './header.style';
import { contactSupport } from './HeaderUtils';
import { logKariusEvent } from '../Analytics/AnalyticsClient';
import { EngagementEvents } from '../Analytics/EngagementEvents';
import Colors from '../theme/colors';
import { themeSelector } from '../theme/ThemeSelector';

const address = `Karius Inc.
975 Island Drive, Suite 101
Redwood City, CA 94065`;

export function SupportMenu() {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [showAlert, setShowAlert] = useState(false);
  const styles = useSupportMenuStyles();

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    logKariusEvent(EngagementEvents.support_tapped);
    setAnchorEl(event.currentTarget);
  };

  const handleAlertClose = (_event: Event | SyntheticEvent<any, Event>, reason?: SnackbarCloseReason) => {
    if (reason === 'clickaway') {
      return;
    }

    setShowAlert(false);
  };

  const onSupportClick = (type: 'phone' | 'email' | 'fax' | 'address') => {
    logKariusEvent(EngagementEvents.support_link_tapped, { link: type });
    contactSupport(type);
    if (type === 'fax' || type === 'address') {
      setShowAlert(true);
    }
  };

  const menuOrigin = themeSelector<'right' | 'left'>({
    white: 'right',
    blue: 'left',
  });

  return (
    <Box
      display='flex'
      flexGrow={1}
      justifyContent={themeSelector({
        white: 'flex-end',
        blue: 'flex-start',
      })}
      flexDirection='row'
      mr={10}
      sx={{
        flex: 1,
        maxWidth: '170px',
        minWidth: '110px',
      }}
    >
      <ButtonBase
        data-test='support-menu-button'
        aria-label='user-menu-button'
        onClick={handleClick}
        sx={{
          width: '100%',
        }}
      >
        <Typography css={styles.button}>Support</Typography>
        {anchorEl ? <ArrowDropUp /> : <ArrowDropDown />}
      </ButtonBase>
      <Menu
        aria-label='support-menu'
        data-test='support-menu'
        keepMounted
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: menuOrigin,
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: menuOrigin,
        }}
        open={!!anchorEl}
        onClose={handleClose}
        style={{
          transform: 'translateY(-10px)',
        }}
      >
        <MenuItem onClick={() => onSupportClick('phone')}>
          <ListItemIcon>
            <Phone color='primary' />
          </ListItemIcon>
          <Typography>(866) 452-7487</Typography>
        </MenuItem>
        <MenuItem onClick={() => onSupportClick('email')}>
          <ListItemIcon>
            <Email color='primary' />
          </ListItemIcon>
          <Typography>help@kariusdx.com</Typography>
        </MenuItem>
        <MenuItem onClick={() => onSupportClick('fax')}>
          <ListItemIcon>
            <ListAltOutlined color='primary' />
          </ListItemIcon>
          <Typography>Fax: (866) 246-6567</Typography>
        </MenuItem>
        <MenuItem onClick={() => onSupportClick('address')}>
          <Box display='flex'>
            <ListItemIcon>
              <MarkunreadMailbox color='primary' />
            </ListItemIcon>
            <Box display='flex' flexDirection='column'>
              <Typography variant='body1'>Mailing Address</Typography>
              <Typography variant='body2' style={{ whiteSpace: 'pre-line', lineHeight: '20px' }}>
                {address}
              </Typography>
            </Box>
          </Box>
        </MenuItem>
      </Menu>
      <Snackbar open={showAlert} onClose={handleAlertClose} autoHideDuration={3000}>
        <Alert severity='info' onClose={handleAlertClose}>
          Text copied to clipboard
        </Alert>
      </Snackbar>
    </Box>
  );
}

export function SupportModal() {
  const matchesXs = useMediaQuery<Theme>((theme) => {
    return theme.breakpoints.down('sm');
  });

  const [showAlert, setShowAlert] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const styles = useSupportMenuStyles();

  const handleAlertClose = (_event: Event | SyntheticEvent<any, Event>, reason?: SnackbarCloseReason) => {
    if (reason === 'clickaway') {
      return;
    }

    setShowAlert(false);
  };

  const onSupportClick = (type: 'phone' | 'email' | 'fax' | 'address') => {
    logKariusEvent(EngagementEvents.support_link_tapped, { link: type });
    contactSupport(type);
    if (type === 'fax' || type === 'address') {
      setShowAlert(true);
    }
  };

  return (
    <>
      {matchesXs ? (
        <Help
          sx={{
            zIndex: 100,
          }}
          onClick={() => {
            logKariusEvent(EngagementEvents.support_tapped);
            setShowModal(true);
          }}
        />
      ) : (
        <Button
          startIcon={<Phone />}
          variant='outlined'
          color='inherit'
          onClick={() => {
            logKariusEvent(EngagementEvents.support_tapped);
            setShowModal(true);
          }}
        >
          Support
        </Button>
      )}
      <Modal
        aria-label='support-modal'
        data-test='support-modal'
        open={showModal}
        onClose={() => setShowModal(false)}
        closeAfterTransition
        sx={{
          '& .MuiBackdrop-root': {
            backgroundColor: Colors.transparentGray,
          },
        }}
        BackdropProps={{
          timeout: 500,
        }}
      >
        <>
          <Fade in={showModal}>
            <Paper elevation={3} css={styles.paperContainer}>
              <List css={styles.list}>
                <ListItem>
                  <Typography css={styles.title} variant='h6'>
                    Support
                  </Typography>
                </ListItem>
                <ListItem button onClick={() => onSupportClick('phone')}>
                  <ListItemAvatar>
                    <Avatar css={styles.primaryIcon}>
                      <Phone />
                    </Avatar>
                  </ListItemAvatar>
                  <Typography>(866) 452-7487</Typography>
                </ListItem>
                <ListItem button onClick={() => onSupportClick('email')}>
                  <ListItemAvatar>
                    <Avatar css={styles.primaryIcon}>
                      <Email />
                    </Avatar>
                  </ListItemAvatar>
                  <Typography>help@kariusdx.com</Typography>
                </ListItem>
                <Divider />
                <ListItem button onClick={() => onSupportClick('fax')}>
                  <ListItemAvatar>
                    <Avatar css={styles.secondaryIcon}>
                      <ListAltOutlined color='primary' />
                    </Avatar>
                  </ListItemAvatar>
                  <Typography>Fax: (866) 246-6567</Typography>
                </ListItem>
                <ListItem button onClick={() => onSupportClick('address')}>
                  <Box display='flex' minWidth='280px'>
                    <ListItemAvatar>
                      <Avatar css={styles.secondaryIcon}>
                        <MarkunreadMailbox color='primary' />
                      </Avatar>
                    </ListItemAvatar>
                    <Box display='flex' flexDirection='column'>
                      <Typography variant='body1'>Mailing Address</Typography>
                      <Typography variant='body2' style={{ whiteSpace: 'pre-line', lineHeight: '20px' }}>
                        {address}
                      </Typography>
                    </Box>
                  </Box>
                </ListItem>
                <Box display='flex' justifyContent='flex-end' p={3}>
                  <Button variant='text' onClick={() => setShowModal(false)} css={styles.close}>
                    Close
                  </Button>
                </Box>
              </List>
            </Paper>
          </Fade>
          <Snackbar open={showAlert} onClose={handleAlertClose} autoHideDuration={3000}>
            <Alert severity='info' onClose={handleAlertClose}>
              Text copied to clipboard
            </Alert>
          </Snackbar>
        </>
      </Modal>
    </>
  );
}
